import React, { forwardRef, useState, useEffect, useRef, useCallback } from 'react';
import { formatTime } from '../../utils/timeFormatter';
import { apiClient } from '../../../../api/http.api';
import {
  PlayIcon,
  PauseIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  ArrowPathIcon,
  MusicalNoteIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from '@heroicons/react/24/solid';
import styles from './styles.module.scss';

/**
 * Video/Audio Player Component
 * 
 * Custom media player with controls for transcript editing
 * Supports both video and audio files (including MP3)
 */
const VideoPlayer = forwardRef(
  (
    {
      src,
      isPlaying: externalIsPlaying,
      onTimeUpdate,
      onPlay,
      onPause,
      onSeeked,
      onLoadedMetadata,
      onFullscreenToggle: externalOnFullscreenToggle,
      className = '',
    },
    ref
  ) => {
    const [internalIsPlaying, setInternalIsPlaying] = useState(false);
    
    // Use external isPlaying if provided, otherwise use internal state
    const isPlaying = externalIsPlaying !== undefined ? externalIsPlaying : internalIsPlaying;
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [isLoaded, setIsLoaded] = useState(false);
    const [blobUrl, setBlobUrl] = useState(null);
    const [videoError, setVideoError] = useState(null);
    const [isAudio, setIsAudio] = useState(false);
    const [hasVideoTrack, setHasVideoTrack] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const playerContainerRef = useRef(null);

    /**
     * Detect if source is an audio file based on extension or MIME type
     */
    const detectMediaType = (url) => {
      if (!url) return false;
      
      // Check file extension
      const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.wma'];
      const urlLower = url.toLowerCase();
      
      return audioExtensions.some(ext => urlLower.includes(ext));
    };

  /**
   * Load video with authentication - using direct URL streaming
   * Video will stream directly from server using HTTP Range requests
   */
  useEffect(() => {
    if (!src) {
      return;
    }

    // Detect if it's an audio file based on extension
    const isAudioFile = detectMediaType(src);
    setIsAudio(isAudioFile);

    // If src is already a blob URL or data URL, use it directly
    if (src.startsWith('blob:') || src.startsWith('data:')) {
      setBlobUrl(src);
      setIsLoaded(false); // Will be set to true when video loads
      return;
    }

    // For API endpoints, the src should already include the token in query parameter
    // (added by parent component). Use it directly for streaming.
    // HTML5 video element will automatically use Range requests for efficient streaming.
    setBlobUrl(src);
    setIsLoaded(false); // Will be set to true when video loads
    setVideoError(null);
    setLoadingProgress(0);
    
    console.log('Setting up video streaming URL:', src);
  }, [src]);

    /**
     * Cleanup old blob URLs when blobUrl changes (only for blob: URLs we created)
     */
    useEffect(() => {
      const currentBlobUrl = blobUrl;
      
      return () => {
        // Only revoke blob URLs we created (not external streaming URLs)
        if (currentBlobUrl && currentBlobUrl.startsWith('blob:') && !src?.startsWith('blob:')) {
          // Small delay to ensure video element isn't still using it
          setTimeout(() => {
            try {
              URL.revokeObjectURL(currentBlobUrl);
              console.log('Cleaned up old blob URL:', currentBlobUrl);
            } catch (error) {
              // Ignore errors if URL was already revoked
              console.log('Blob URL already revoked or invalid:', currentBlobUrl);
            }
          }, 100);
        }
      };
    }, [blobUrl, src]);

    /**
     * Handle play/pause toggle
     */
    const handlePlayPause = () => {
      if (!ref?.current) return;

      if (ref.current.paused) {
        ref.current.play();
      } else {
        ref.current.pause();
      }
    };

    /**
     * Handle seek
     */
    const handleSeek = (e) => {
      if (!ref?.current) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = x / rect.width;
      const time = percentage * duration;

      ref.current.currentTime = time;
      setCurrentTime(time);
    };

    /**
     * Handle volume change
     */
    const handleVolumeChange = (e) => {
      if (!ref?.current) return;

      const newVolume = parseFloat(e.target.value);
      ref.current.volume = newVolume;
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    };

    /**
     * Handle mute toggle
     */
    const handleMuteToggle = () => {
      if (!ref?.current) return;

      const newMuted = !isMuted;
      ref.current.muted = newMuted;
      setIsMuted(newMuted);
    };

    /**
     * Handle playback rate change
     */
    const handlePlaybackRateChange = () => {
      if (!ref?.current) return;

      const rates = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
      const currentIndex = rates.indexOf(playbackRate);
      const nextIndex = (currentIndex + 1) % rates.length;
      const newRate = rates[nextIndex];

      ref.current.playbackRate = newRate;
      setPlaybackRate(newRate);
    };

    /**
     * Handle skip (forward/backward)
     */
    const handleSkip = (seconds) => {
      if (!ref?.current) return;

      const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
      ref.current.currentTime = newTime;
      setCurrentTime(newTime);
    };

    /**
     * Handle fullscreen toggle
     */
    const handleFullscreenToggle = useCallback(async () => {
      const container = playerContainerRef.current;
      if (!container) {
        console.log('[VideoPlayer] No container ref for fullscreen');
        return;
      }

      try {
        const isCurrentlyFullscreen = !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement
        );

        if (!isCurrentlyFullscreen) {
          // Enter fullscreen
          console.log('[VideoPlayer] Entering fullscreen');
          if (container.requestFullscreen) {
            await container.requestFullscreen();
          } else if (container.webkitRequestFullscreen) {
            // Safari
            await container.webkitRequestFullscreen();
          } else if (container.mozRequestFullScreen) {
            // Firefox
            await container.mozRequestFullScreen();
          } else if (container.msRequestFullscreen) {
            // IE/Edge
            await container.msRequestFullscreen();
          }
        } else {
          // Exit fullscreen
          console.log('[VideoPlayer] Exiting fullscreen');
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            // Safari
            await document.webkitExitFullscreen();
          } else if (document.mozCancelFullScreen) {
            // Firefox
            await document.mozCancelFullScreen();
          } else if (document.msExitFullscreen) {
            // IE/Edge
            await document.msExitFullscreen();
          }
        }
      } catch (error) {
        console.error('[VideoPlayer] Error toggling fullscreen:', error);
      }
    }, []);

    /**
     * Video event listeners
     */
    useEffect(() => {
      const video = ref?.current;
      if (!video) return;

      const handleTimeUpdateEvent = () => {
        setCurrentTime(video.currentTime);
        onTimeUpdate?.(video.currentTime);
      };

      const handlePlayEvent = () => {
        setInternalIsPlaying(true);
        onPlay?.();
      };

      const handlePauseEvent = () => {
        setInternalIsPlaying(false);
        onPause?.();
      };

      const handleLoadedMetadataEvent = () => {
        setDuration(video.duration);
        setIsLoaded(true);
        
        // Check if the media has a video track
        // If videoWidth and videoHeight are 0, it's audio only
        const hasVideo = video.videoWidth > 0 && video.videoHeight > 0;
        setHasVideoTrack(hasVideo);
        
        console.log('Media loaded:', {
          duration: video.duration,
          hasVideo,
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight
        });
        
        onLoadedMetadata?.(video.duration);
      };

      const handleSeekedEvent = () => {
        onSeeked?.(video.currentTime);
      };

      video.addEventListener('timeupdate', handleTimeUpdateEvent);
      video.addEventListener('play', handlePlayEvent);
      video.addEventListener('pause', handlePauseEvent);
      video.addEventListener('loadedmetadata', handleLoadedMetadataEvent);
      video.addEventListener('seeked', handleSeekedEvent);

      // Initial setup
      if (video.readyState >= 1) {
        setDuration(video.duration);
        setIsLoaded(true);
      }

      return () => {
        video.removeEventListener('timeupdate', handleTimeUpdateEvent);
        video.removeEventListener('play', handlePlayEvent);
        video.removeEventListener('pause', handlePauseEvent);
        video.removeEventListener('loadedmetadata', handleLoadedMetadataEvent);
        video.removeEventListener('seeked', handleSeekedEvent);
      };
    }, [ref, onTimeUpdate, onPlay, onPause, onLoadedMetadata, onSeeked]);

    /**
     * Expose handleFullscreenToggle to parent via ref
     */
    useEffect(() => {
      if (externalOnFullscreenToggle && typeof externalOnFullscreenToggle === 'object') {
        console.log('[VideoPlayer] Setting fullscreen ref');
        externalOnFullscreenToggle.current = handleFullscreenToggle;
      } else {
        console.log('[VideoPlayer] externalOnFullscreenToggle is not a ref object:', externalOnFullscreenToggle);
      }
    }, [externalOnFullscreenToggle, handleFullscreenToggle]);

    /**
     * Fullscreen change event listener
     */
    useEffect(() => {
      const handleFullscreenChange = () => {
        const isCurrentlyFullscreen = !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement
        );
        setIsFullscreen(isCurrentlyFullscreen);
      };

      document.addEventListener('fullscreenchange', handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.addEventListener('mozfullscreenchange', handleFullscreenChange);
      document.addEventListener('MSFullscreenChange', handleFullscreenChange);

      return () => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
        document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      };
    }, []);

    // Determine if we should show audio UI (either detected as audio or no video track)
    const showAudioUI = isAudio || !hasVideoTrack;

    return (
      <div ref={playerContainerRef} className={`bg-black rounded-lg overflow-hidden ${className}`}>
        {/* Media Element */}
        <div className={`relative w-full ${showAudioUI ? 'h-48' : 'aspect-video'} ${showAudioUI ? styles.audioContainer : 'bg-black'}`}>
          <video
            key={src}
            ref={ref}
            src={blobUrl || undefined}
            className={`w-full h-full ${showAudioUI ? 'hidden' : ''}`}
            playsInline
            preload="metadata"
            onError={(e) => {
              console.error('Media element error:', e);
              console.error('Media blobUrl:', blobUrl);
              console.error('Video element:', ref?.current);
              
              // Try to get more specific error information
              let errorMsg = 'Failed to load media file';
              if (ref?.current?.error) {
                const mediaError = ref.current.error;
                switch (mediaError.code) {
                  case 1: // MEDIA_ERR_ABORTED
                    errorMsg = 'Media loading was aborted';
                    break;
                  case 2: // MEDIA_ERR_NETWORK
                    errorMsg = 'Network error while loading media';
                    break;
                  case 3: // MEDIA_ERR_DECODE
                    errorMsg = 'Media file is corrupted or unsupported format';
                    break;
                  case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
                    errorMsg = 'Media format not supported by your browser';
                    break;
                  default:
                    errorMsg = mediaError.message || 'Unknown media error';
                }
              }
              
              setVideoError(errorMsg);
              setIsLoaded(false);
            }}
          />
          
          {/* Audio UI Overlay */}
          {showAudioUI && isLoaded && !videoError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="mb-4 relative h-32 flex items-center justify-center">
                  <MusicalNoteIcon className={`h-24 w-24 text-blue-500 ${styles.audioIcon} ${isPlaying ? styles.playing : ''}`} />
                  {isPlaying && (
                    <div className="absolute bottom-0 flex gap-1 items-end justify-center">
                      <div className={styles.audioWaveBar} style={{ height: '30px' }}></div>
                      <div className={styles.audioWaveBar} style={{ height: '40px' }}></div>
                      <div className={styles.audioWaveBar} style={{ height: '35px' }}></div>
                      <div className={styles.audioWaveBar} style={{ height: '45px' }}></div>
                      <div className={styles.audioWaveBar} style={{ height: '30px' }}></div>
                    </div>
                  )}
                </div>
                <p className="text-white text-lg font-medium">Audio Player</p>
                <p className="text-gray-400 text-sm mt-1">
                  {isPlaying ? 'Playing...' : 'Paused'}
                </p>
              </div>
            </div>
          )}
          
          {/* Loading States */}
          {!isLoaded && blobUrl && !videoError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <ArrowPathIcon className="h-12 w-12 text-white animate-spin mx-auto mb-2" />
                <p className="text-white text-sm">Loading {showAudioUI ? 'audio' : 'video'}...</p>
              </div>
            </div>
          )}
          
          {!blobUrl && !videoError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
              <div className="text-center w-64">
                <ArrowPathIcon className="h-12 w-12 text-white animate-spin mx-auto mb-3" />
                <p className="text-white text-sm mb-3">Preparing {showAudioUI ? 'audio' : 'video'}...</p>
                {loadingProgress > 0 && loadingProgress < 100 && (
                  <>
                    <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${loadingProgress}%` }}
                      />
                    </div>
                    <p className="text-gray-400 text-xs">{loadingProgress}% downloaded</p>
                  </>
                )}
              </div>
            </div>
          )}
          
          {videoError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
              <div className="text-center max-w-md px-6">
                <div className="mb-4">
                  <svg className="h-16 w-16 text-red-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Failed to Load Media</h3>
                <p className="text-red-400 text-sm mb-1">{videoError}</p>
                <p className="text-gray-400 text-xs mb-4">
                  The media file could not be loaded. This might be because:
                  <br />• The file is missing or corrupted
                  <br />• Network connection issue
                  <br />• Unsupported file format
                </p>
                <button
                  onClick={() => {
                    setVideoError(null);
                    setBlobUrl(null);
                    setIsLoaded(false);
                    // Force re-fetch by updating state
                    window.location.reload();
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Retry Loading
                </button>
                <p className="text-gray-500 text-xs mt-3">Check browser console (F12) for technical details</p>
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="bg-gray-900 p-4 space-y-3">
          {/* Timeline */}
          <div
            className="relative h-2 bg-gray-700 rounded-full cursor-pointer group"
            onClick={handleSeek}
          >
            <div
              className="absolute h-full bg-blue-600 rounded-full"
              style={{ width: `${(currentTime / duration) * 100}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `calc(${(currentTime / duration) * 100}% - 8px)` }}
            />
          </div>

          {/* Controls Bar */}
          <div className="flex items-center justify-between gap-4">
            {/* Left: Play/Pause */}
            <div className="flex items-center gap-2">
              <button
                onClick={handlePlayPause}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
              >
                {isPlaying ? (
                  <PauseIcon className="h-6 w-6 text-white" />
                ) : (
                  <PlayIcon className="h-6 w-6 text-white" />
                )}
              </button>
            </div>

            {/* Center: Time Display */}
            <div className="text-sm text-white font-mono">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>

            {/* Right: Volume & Speed */}
            <div className="flex items-center gap-3">
              {/* Volume */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleMuteToggle}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted || volume === 0 ? (
                    <SpeakerXMarkIcon className="h-5 w-5 text-white" />
                  ) : (
                    <SpeakerWaveIcon className="h-5 w-5 text-white" />
                  )}
                </button>

                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="w-20 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
              </div>

              {/* Fullscreen */}
              <button
                onClick={handleFullscreenToggle}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                {isFullscreen ? (
                  <ArrowsPointingInIcon className="h-5 w-5 text-white" />
                ) : (
                  <ArrowsPointingOutIcon className="h-5 w-5 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;


